/**
 * Two-Joint Pole multiplayer bot (Player 2).
 * Mirrors game_engines/two_joint_pole/two_joint_pole_player2_bot.py
 */

const W_POLE1 = 1.25;
const W_POLE2 = 0.72;
const W_POLE1_VEL = 0.48;
const W_POLE2_VEL = 0.32;
const W_CART_POS = 0.12;
const W_CART_VEL = 0.11;
const STAY_THRESHOLD = 0.01;

/** Match GM arm-geometry punch reach (~1.58 m cart-cart). */
const PUNCH_REACH = 1.58;
const TRACK_LIMIT = 2.4;
const EDGE_MARGIN = 0.35;
const ANGLE_DANGER = 0.2;
const VEL_DANGER = 0.75;
const ANGLE_STABLE = 0.09;
const VEL_STABLE = 0.4;

const DIFFICULTY_PRESETS = {
  easy: {
    approachGain: 0.28,
    holdGain: 0.18,
    optimalRange: 1.05,
    punchRange: 1.15,
    punchOppTilt: 0.1,
    punchChance: 0.62,
    mistakeChance: 0.03,
    edgeBias: 0.55,
  },
  medium: {
    approachGain: 0.4,
    holdGain: 0.28,
    optimalRange: 0.95,
    punchRange: 1.35,
    punchOppTilt: 0.06,
    punchChance: 0.88,
    mistakeChance: 0.01,
    edgeBias: 0.75,
  },
  hard: {
    approachGain: 0.55,
    holdGain: 0.4,
    optimalRange: 0.85,
    punchRange: 1.52,
    punchOppTilt: 0.04,
    punchChance: 1.0,
    mistakeChance: 0.0,
    edgeBias: 1.0,
  },
};

const num = (physics, key, fallback = 0) => {
  const v = Number(physics?.[key]);
  return Number.isFinite(v) ? v : fallback;
};

export const normalizeBotDifficulty = (difficulty) => {
  const key = String(difficulty ?? 'hard').trim().toLowerCase();
  if (key === 'easy' || key === 'low' || key === '0') return 'easy';
  if (key === 'medium' || key === 'med' || key === 'balanced' || key === '1') return 'medium';
  return 'hard';
};

/** Map Easy / Balanced / Hard physics packs → bot skill (even Easy stays competent). */
export const botDifficultyFromPhysicsPreset = (presetId) => {
  const id = String(presetId ?? '').toLowerCase();
  if (id === 'easy') return 'easy';
  if (id === 'balanced' || id === 'medium') return 'medium';
  if (id === 'hard') return 'hard';
  return 'hard';
};

const poleTilt = (physics) => Math.abs(num(physics, 'pole1_angle')) + 0.7 * Math.abs(num(physics, 'pole2_angle'));
const poleSpin = (physics) => Math.abs(num(physics, 'pole1_velocity')) + 0.55 * Math.abs(num(physics, 'pole2_velocity'));
const isDanger = (physics) => poleTilt(physics) > ANGLE_DANGER || poleSpin(physics) > VEL_DANGER;
const isStable = (physics) => poleTilt(physics) <= ANGLE_STABLE && poleSpin(physics) <= VEL_STABLE;

const balanceSignal = (physics, approachGain = 0, targetX = 0, edgeBias = 0) => {
  const cartPos = num(physics, 'cart_position');
  let edge = 0;
  if (edgeBias > 0) {
    if (cartPos > TRACK_LIMIT - EDGE_MARGIN) {
      edge = -(cartPos - (TRACK_LIMIT - EDGE_MARGIN)) * edgeBias;
    } else if (cartPos < -(TRACK_LIMIT - EDGE_MARGIN)) {
      edge = (-(TRACK_LIMIT - EDGE_MARGIN) - cartPos) * edgeBias;
    }
  }
  return (
    W_POLE1 * num(physics, 'pole1_angle') +
    W_POLE2 * num(physics, 'pole2_angle') +
    W_POLE1_VEL * num(physics, 'pole1_velocity') +
    W_POLE2_VEL * num(physics, 'pole2_velocity') +
    W_CART_POS * cartPos +
    W_CART_VEL * num(physics, 'cart_velocity') +
    approachGain * (targetX - cartPos) +
    edge
  );
};

const signalToAction = (signal) => {
  if (Math.abs(signal) < STAY_THRESHOLD) return 'stay';
  return signal > 0 ? 'right' : 'left';
};

const standOffTarget = (own, opponent, optimalRange) => {
  const ownX = num(own, 'cart_position');
  const oppX = num(opponent, 'cart_position');
  const dx = oppX - ownX;
  const dist = Math.abs(dx);
  if (dist < 1e-6) return ownX + (ownX <= 0 ? 0.4 : -0.4);
  const facing = dx > 0 ? 1 : -1;
  return oppX - facing * optimalRange;
};

const shouldPunch = (own, opponent, cfg, randomFn) => {
  if (num(own, 'punch_cooldown') > 0) return false;
  if (!isStable(own)) return false;
  if (Math.abs(num(own, 'cart_position')) > TRACK_LIMIT - EDGE_MARGIN * 0.6) return false;

  const distance = Math.abs(num(own, 'cart_position') - num(opponent, 'cart_position'));
  const maxRange = Math.min(PUNCH_REACH, cfg.punchRange);
  if (distance > maxRange) return false;

  const oppTilt = poleTilt(opponent);
  const opportunistic = distance <= cfg.optimalRange * 1.15;
  const vulnerable = oppTilt >= cfg.punchOppTilt || poleSpin(opponent) > 0.3;
  const inReach = distance <= maxRange * 0.92;
  if (!vulnerable && !opportunistic && !inReach) return false;

  return randomFn() <= cfg.punchChance;
};

/**
 * @param {object} ownPhysics - bot's physics
 * @param {object} opponentPhysics - human / other player
 * @param {{ difficulty?: string, random?: () => number }} [options]
 * @returns {'left'|'right'|'stay'|'punch'}
 */
export function chooseBotAction(ownPhysics, opponentPhysics, options = {}) {
  const level = normalizeBotDifficulty(options.difficulty);
  const cfg = DIFFICULTY_PRESETS[level];
  const randomFn = typeof options.random === 'function' ? options.random : Math.random;

  if (isDanger(ownPhysics)) {
    return signalToAction(balanceSignal(ownPhysics, 0, 0, cfg.edgeBias));
  }

  if (shouldPunch(ownPhysics, opponentPhysics, cfg, randomFn)) {
    return 'punch';
  }

  const tilt = poleTilt(ownPhysics);
  const calm = Math.max(0, 1 - tilt / Math.max(ANGLE_DANGER, 1e-6));
  const targetX = standOffTarget(ownPhysics, opponentPhysics, cfg.optimalRange);
  const dist = Math.abs(num(opponentPhysics, 'cart_position') - num(ownPhysics, 'cart_position'));
  const gain = (dist > cfg.optimalRange * 1.25 ? cfg.approachGain : cfg.holdGain) * calm;

  const action = signalToAction(balanceSignal(ownPhysics, gain, targetX, cfg.edgeBias));
  // High-threshold form so deterministic random=0 never triggers mistakes.
  if (action !== 'stay' && cfg.mistakeChance > 0 && randomFn() >= 1 - cfg.mistakeChance) {
    return 'stay';
  }
  return action;
}

/** Deterministic variant (always takes punch when eligible; no mistakes). */
export function chooseBotActionStatic(ownPhysics, opponentPhysics, difficulty = 'hard') {
  return chooseBotAction(ownPhysics, opponentPhysics, {
    difficulty,
    random: () => 0,
  });
}

export default chooseBotAction;
